import React, { Component } from 'react';
import { connect } from 'react-redux';
import { OffCanvasItem } from '../../components/offCanvasNavigation';
import { getUserCommunity } from '../../store/session/selectors';
import {
  getModule,
  listTypes,
  countActs,
  listAppeals
} from '../../services/fines';

// Cada cuánto se actualiza el número de pendientes del menú.
const PENDING_POLL_MS = 120000;

/**
 * Lo que espera que alguien haga algo: actas por confirmar, actas sin unidad y
 * descargos por responder. { total, title } para el número del menú.
 */
export const pendingSummary = ({ toConfirm, withoutUnit, appeals }) => {
  const parts = [];
  if (toConfirm) {
    parts.push(`${toConfirm} sin emitir`);
  }
  if (withoutUnit) {
    parts.push(`${withoutUnit} sin unidad`);
  }
  if (appeals) {
    parts.push(
      `${appeals} ${appeals === 1 ? 'descargo' : 'descargos'} por responder`
    );
  }
  return {
    total: (toConfirm || 0) + (withoutUnit || 0) + (appeals || 0),
    title: parts.length ? `Infracciones: ${parts.join(' · ')}` : ''
  };
};

/**
 * GET /module dice si la comunidad tiene Multas (lo prende o apaga el staff).
 * Donde ese endpoint todavía no existe (404), se mira como antes: si amercement
 * responde para la comunidad.
 */
export const isModuleEnabled = (token, communityId) =>
  getModule(token, communityId)
    .then(data => Boolean(data && data.enabled))
    .catch(error => {
      if (error && error.response && error.response.status === 404) {
        return listTypes(token, communityId).then(() => true);
      }
      throw error;
    });

/** Item del menu "Multas": solo si el módulo está habilitado para la comunidad. */
export class FinesNavItem extends Component {
  state = { available: false, pending: null };

  componentDidMount() {
    this.mounted = true;
    this.check();
  }

  // The session or the community can arrive after the menu mounts, and the admin
  // can switch community: check again so the item does not stay hidden.
  componentDidUpdate(prevProps) {
    if (
      prevProps.token !== this.props.token ||
      prevProps.communityId !== this.props.communityId
    ) {
      this.check();
    } else if (
      prevProps.currentPath !== this.props.currentPath &&
      this.state.available
    ) {
      // Al cambiar de pantalla (por ejemplo, después de confirmar actas).
      this.fetchPending();
    }
  }

  check() {
    const { token, communityId } = this.props;
    if (!token || !communityId) {
      this.setState({ available: false });
      return;
    }
    const request = isModuleEnabled(token, communityId);
    this.pending = request;
    request
      .then(
        enabled =>
          this.mounted &&
          this.pending === request &&
          this.setState({ available: enabled }, this.loadPending)
      )
      .catch(
        () =>
          this.mounted &&
          this.pending === request &&
          this.setState({ available: false })
      );
  }

  componentWillUnmount() {
    this.mounted = false;
    clearInterval(this.pendingTimer);
  }

  loadPending = async () => {
    clearInterval(this.pendingTimer);
    if (!this.state.available) {
      this.setState({ pending: null });
      return;
    }
    this.pendingTimer = setInterval(this.fetchPending, PENDING_POLL_MS);
    this.fetchPending();
  };

  fetchPending = async () => {
    const { token, communityId } = this.props;
    try {
      // De a uno: amercement tiene pocas conexiones por comunidad.
      const draft = await countActs(token, communityId, { status: 'DRAFT' });
      const matched = await countActs(token, communityId, {
        status: 'MATCHED'
      });
      const withoutUnit = await countActs(token, communityId, {
        status: 'UNMATCHED'
      });
      const appeals = await listAppeals(token, communityId, 'PENDING');
      if (this.mounted && communityId === this.props.communityId) {
        this.setState({
          pending: pendingSummary({
            toConfirm: draft + matched,
            withoutUnit,
            appeals: (appeals || []).length
          })
        });
      }
    } catch (error) {
      if (this.mounted) {
        this.setState({ pending: null });
      }
    }
  };

  render() {
    if (!this.state.available) {
      return null;
    }
    return (
      <OffCanvasItem
        label="Infracciones"
        icon="fines"
        linkTo="/fines"
        isActive={this.props.currentPath.includes('fines')}
        badge={this.state.pending ? this.state.pending.total : 0}
        badgeTitle={this.state.pending ? this.state.pending.title : ''}
      />
    );
  }
}

const mapStateToProps = state => {
  const community = getUserCommunity(state);
  return {
    token: state.session.user && state.session.user.token,
    communityId: community && community.id
  };
};

export default connect(mapStateToProps)(FinesNavItem);
